import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, KeyRound, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authApi } from '@/lib/auth-api';
import { getErrorMessage } from '@/lib/errors';

export function ApiTokensPage() {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('');
  const [issued, setIssued] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const tokens = useQuery({ queryKey: ['api-tokens'], queryFn: authApi.listApiTokens });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['api-tokens'] });

  const create = useMutation({
    mutationFn: () =>
      authApi.createApiToken(name, ['*'], expiresInDays ? Number(expiresInDays) : null),
    onSuccess: (token) => {
      setIssued(token.plain_text_token);
      setName('');
      setExpiresInDays('');
      setCopied(false);
      void invalidate();
    },
  });

  const revoke = useMutation({ mutationFn: authApi.revokeApiToken, onSuccess: invalidate });

  async function copyToken() {
    if (!issued) return;
    await navigator.clipboard.writeText(issued);
    setCopied(true);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Create an API token</CardTitle>
          <CardDescription>
            Use tokens to access the platform API programmatically. The token is shown once. A token
            with no expiry keeps working until you revoke it.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {create.error && <Alert variant="danger">{getErrorMessage(create.error)}</Alert>}

          {issued && (
            <Alert variant="success" className="space-y-2">
              <p className="font-medium">
                Copy your token now. It will not be shown again.
              </p>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 select-all break-all rounded bg-background/60 px-2 py-1 font-mono text-xs">
                  {issued}
                </code>
                <Button size="sm" variant="outline" onClick={copyToken}>
                  <Copy /> {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </Alert>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
            className="flex flex-wrap items-end gap-3"
          >
            <div className="min-w-48 flex-1 space-y-2">
              <Label htmlFor="token-name">Token name</Label>
              <Input
                id="token-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={100}
              />
            </div>
            <div className="w-40 space-y-2">
              <Label htmlFor="token-expiry">
                Expires in days <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Input
                id="token-expiry"
                type="number"
                min={1}
                max={3650}
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending && <Loader2 className="animate-spin" />} Create token
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Your tokens</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {revoke.error && <Alert variant="danger">{getErrorMessage(revoke.error)}</Alert>}

          {tokens.isLoading && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading tokens…
            </p>
          )}

          {tokens.isError && <Alert variant="danger">{getErrorMessage(tokens.error)}</Alert>}

          {tokens.data?.map((token) => (
            <div
              key={token.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-muted/20 px-4 py-3"
            >
              <div className="flex min-w-0 items-start gap-3">
                <KeyRound className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{token.name}</p>
                  <p className="font-mono text-xs text-muted-foreground">{token.prefix}…</p>
                  <p className="text-xs text-muted-foreground">
                    {token.last_used_at
                      ? `Last used ${new Date(token.last_used_at).toLocaleString()}`
                      : 'Never used'}
                    {token.expires_at &&
                      ` · expires ${new Date(token.expires_at).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {token.revoked ? (
                  <Badge variant="muted">Revoked</Badge>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revoke.mutate(token.id)}
                    disabled={revoke.isPending}
                  >
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          ))}

          {tokens.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">You haven't created any API tokens yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
